<template>
  <!-- .admin-shell is the gentelella token scope (admin-theme.scss); the store
       uses it too. data-theme lives here rather than on <html> so dark mode
       can never reach the public site. -->
  <div class="admin-shell"
    :data-theme="theme">
    <aside class="sidebar"
      :class="{ open: mobileOpen }"
      aria-label="Navigatsioon">
      <RouterLink class="sidebar-brand"
        to="/"
        title="Avaleht">
        <img class="brand-mark"
          src="@/assets/logo.svg"
          alt="Aktuaalne">
        <div class="brand-name">Aktuaalne OÜ <small>{{ brandSuffix }}</small></div>
      </RouterLink>

      <nav class="sidebar-nav">
        <div class="nav-group">
          <template v-for="item in nav"
            :key="item.id">
            <div v-if="item.children"
              class="nav-tree"
              :class="{ open: openGroups.includes(item.id), 'has-active': isGroupActive(item) }">
              <component :is="linksGroup(item) ? 'RouterLink' : 'button'"
                v-bind="linksGroup(item) ? { to: item.to } : { type: 'button' }"
                class="nav-link nav-toggle"
                :class="{ active: active === item.id }"
                :aria-expanded="openGroups.includes(item.id) ? 'true' : 'false'"
                @click="linksGroup(item) || toggleGroup(item.id)">
                <i :class="[item.icon, 'icon']"
                  aria-hidden="true" />
                <span class="nav-text">{{ item.label }}</span>
                <button v-if="linksGroup(item)"
                  type="button"
                  class="nav-chev-btn"
                  :aria-label="openGroups.includes(item.id) ? 'Peida alamkategooriad' : 'Näita alamkategooriaid'"
                  @click.prevent.stop="toggleGroup(item.id)">
                  <svg class="nav-chev"
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
                <svg v-else
                  class="nav-chev"
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2">
                  <path d="M9 6l6 6-6 6" />
                </svg>
              </component>

              <!-- Rail mode shows this as a flyout; the title replaces the
                   tooltip, so the toggle has no data-rail-label. -->
              <div class="nav-sub">
                <div class="nav-sub-inner">
                  <component :is="linksGroup(item) ? 'RouterLink' : 'div'"
                    v-bind="linksGroup(item) ? { to: item.to } : {}"
                    class="nav-sub-title">{{ item.label }}</component>
                  <RouterLink v-for="child in item.children"
                    :key="child.id"
                    class="nav-sublink"
                    :class="{ active: active === child.id }"
                    :to="child.to">{{ child.label }}</RouterLink>
                </div>
              </div>
            </div>

            <RouterLink v-else
              class="nav-link"
              :class="{ active: active === item.id }"
              :to="item.to"
              :data-rail-label="item.label">
              <i :class="[item.icon, 'icon']"
                aria-hidden="true" />
              <span class="nav-text">{{ item.label }}</span>
            </RouterLink>
          </template>
        </div>
      </nav>

      <div v-if="$slots['sidebar-footer']"
        class="sidebar-footer">
        <slot name="sidebar-footer"
          :rail="railCollapsed" />
      </div>
      <div v-else-if="note"
        class="sidebar-footer sidebar-note">{{ note }}</div>
    </aside>

    <div class="sidebar-backdrop"
      :hidden="!mobileOpen"
      @click="closeMobile" />

    <header class="topbar">
      <div class="topbar-left">
        <button class="sidebar-toggle"
          type="button"
          aria-label="Lülita menüü"
          :aria-expanded="mobileOpen ? 'true' : 'false'"
          @click="toggleSidebar">
          <svg width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="1.5">
            <path d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
        <nav class="breadcrumb"
          aria-label="Asukoht">
          <slot name="breadcrumb">
            <span class="current"
              aria-current="page">{{ title }}</span>
          </slot>
        </nav>
      </div>

      <div class="topbar-right">
        <slot name="topbar-right" />
      </div>
    </header>

    <main class="main">
      <div class="page-wrapper">
        <slot />
      </div>

      <footer class="footer">
        <slot name="footer" />
      </footer>
    </main>
  </div>
</template>

<script>
const RAIL_KEY = 'aktuaalne:sidebar-rail'
const DESKTOP = '(min-width: 769px)'

export default {
  name: 'app-shell',
  props: {
    // { id, label, icon, to?, children?: [{ id, label, to }] }
    nav: { type: Array, default: () => [] },
    active: { type: [String, Number, null], default: null },
    theme: { type: String, default: 'light' },
    title: { type: String, default: '' },
    brandSuffix: { type: String, default: '' },
    // Sidebar footer text; hidden while the sidebar is a rail or a drawer.
    note: { type: String, default: '' },
    // A group parent with a `to` navigates and only its chevron toggles the
    // children; off, the whole row toggles.
    linkGroups: { type: Boolean, default: false },
  },
  data() {
    return {
      openGroups: [],
      mobileOpen: false,
      railCollapsed: false,
      desktopQuery: null,
    }
  },
  watch: {
    '$route.path'() {
      this.closeMobile()
      this.revealActiveGroup()
    },
    active: 'revealActiveGroup',
    nav: { handler: 'revealActiveGroup', immediate: true },
    railCollapsed: 'applyBodyClasses',
    mobileOpen: 'applyBodyClasses',
  },
  mounted() {
    this.desktopQuery = window.matchMedia(DESKTOP)
    this.railCollapsed = this.desktopQuery.matches && localStorage.getItem(RAIL_KEY) === '1'
    this.applyBodyClasses()

    this.desktopQuery.addEventListener('change', this.onViewportChange)
    document.addEventListener('keydown', this.onKeydown)
  },
  unmounted() {
    this.desktopQuery?.removeEventListener('change', this.onViewportChange)
    document.removeEventListener('keydown', this.onKeydown)
    document.body.classList.remove('admin-mode', 'sidebar-rail', 'sidebar-open')
  },
  methods: {
    linksGroup(item) {
      return this.linkGroups && !!item.to
    },
    isGroupActive(item) {
      return item.id === this.active || item.children.some((child) => child.id === this.active)
    },
    // Accordion: one group open at a time. A deep link opens with its group
    // unfolded.
    revealActiveGroup() {
      const active = this.nav.find((item) => item.children && this.isGroupActive(item))
      if (active) this.openGroups = [active.id]
    },
    toggleGroup(id) {
      // Rail mode uses the hover flyout; don't change the inline state.
      if (this.railCollapsed) return
      this.openGroups = this.openGroups.includes(id) ? [] : [id]
    },
    applyBodyClasses() {
      // admin-mode lifts the `body { overflow: hidden }` that index.html sets
      // for the public site; gentelella's shell scrolls the document.
      document.body.classList.add('admin-mode')
      document.body.classList.toggle('sidebar-rail', this.railCollapsed)
      document.body.classList.toggle('sidebar-open', this.mobileOpen)
    },
    closeMobile() {
      this.mobileOpen = false
    },
    // One button, two behaviours: collapse to an icon rail on desktop, slide the
    // drawer in and out on mobile. Matches what gentelella's CSS expects.
    toggleSidebar() {
      if (this.desktopQuery?.matches) {
        this.railCollapsed = !this.railCollapsed
        localStorage.setItem(RAIL_KEY, this.railCollapsed ? '1' : '0')
      } else {
        this.mobileOpen = !this.mobileOpen
      }
    },
    onViewportChange(e) {
      if (e.matches) {
        this.mobileOpen = false
        this.railCollapsed = localStorage.getItem(RAIL_KEY) === '1'
      } else {
        this.railCollapsed = false
      }
    },
    onKeydown(e) {
      if (e.key === 'Escape' && this.mobileOpen) this.closeMobile()
    },
  },
}
</script>

<style lang="scss" scoped>
// Replaces gentelella's .brand-icon; 28px tall so rail mode still centres it.
.brand-mark {
  height: 28rem;
  width: auto;
  flex-shrink: 0;
  display: block;
}

.brand-name {
  font-weight: 400;
}

// A link to the front page; app.scss reverts bare links to the browser's blue.
.sidebar-brand {
  color: inherit;
  text-decoration: none;
}
</style>
